import type { Tile } from '../../hex/types';
import { getGameConfig } from '../../../config/gameConfig';
import { createHouses } from './houseFactory';
import { createToolItem } from './toolFactory';
import { createResourceStats, getResourceStatsWindow } from './resourceStatsFactory';
import { extractRegionResources } from './regionResourcesFactory';
import { createSettlementBuilding } from './buildingFactory';
import { buildSettlementName } from '../utils/settlementNameGenerator';
import type { Settlement } from '../types';
import { setPopulation } from '../manager/populationManager';

export function createStarterSettlement(tile: Tile, regionTiles: Tile[] = [tile]): Settlement {
    const config = getGameConfig();
    const resourceWindow = getResourceStatsWindow(config);
    const initialBuildings = config.initial.settlement.buildings;
    const settlement: Settlement = {
        id: `settlement_${tile.id}`,
        tileId: tile.id,
        tilePosition: { ...tile.position },
        name: buildSettlementName(config.map.seed ?? 0, tile.position),
        population: 0,
        people: [],
        tier: config.initial.settlement.tier,
        buildings: {
            houses: initialBuildings.houses,
            stoneHouse: initialBuildings.stoneHouse ?? 0,
            storage: initialBuildings.storage ?? false,
            lumberjack: createSettlementBuilding(
                initialBuildings.lumberjack,
                config.buildings.lumberjackCamp,
            ),
            sawmill: createSettlementBuilding(initialBuildings.sawmill, config.buildings.sawmill),
            farm: createSettlementBuilding(initialBuildings.farm, config.buildings.farm),
            huntingHut: createSettlementBuilding(
                initialBuildings.huntingHut,
                config.buildings.huntingHut,
            ),
            fishingHut: createSettlementBuilding(
                initialBuildings.fishingHut,
                config.buildings.fishingHut,
            ),
            smokehouse: createSettlementBuilding(
                initialBuildings.smokehouse,
                config.buildings.smokehouse,
            ),
            weaver: createSettlementBuilding(initialBuildings.weaver, config.buildings.weaver),
            blacksmith: createSettlementBuilding(
                initialBuildings.blacksmith,
                config.buildings.blacksmith,
            ),
            goldMine: createSettlementBuilding(
                initialBuildings.goldMine,
                config.buildings.goldMine,
            ),
            mint: createSettlementBuilding(initialBuildings.mint, config.buildings.mint),
            caravanPost: createSettlementBuilding(
                initialBuildings.caravanPost,
                config.buildings.caravanPost,
            ),
            stable: createSettlementBuilding(initialBuildings.stable, config.buildings.stable),
            quarry: createSettlementBuilding(initialBuildings.quarry, config.buildings.quarry),
            masonry: createSettlementBuilding(initialBuildings.masonry, config.buildings.masonry),
            charcoalPit: createSettlementBuilding(
                initialBuildings.charcoalPit,
                config.buildings.charcoalPit,
            ),
            ironMine: createSettlementBuilding(
                initialBuildings.ironMine,
                config.buildings.ironMine,
            ),
            smelter: createSettlementBuilding(initialBuildings.smelter, config.buildings.smelter),
            barracks: createSettlementBuilding(
                initialBuildings.barracks,
                config.buildings.barracks,
            ),
        },
        jobs: {
            lumberjacks: 0,
            assistants: 0,
            sawmillers: 0,
            foresters: 0,
            farmers: 0,
            hunters: 0,
            fishers: 0,
            smokers: 0,
            weavers: 0,
            smiths: 0,
            goldMiners: 0,
            minters: 0,
            builders: 0,
            quarrymen: 0,
            masons: 0,
            burners: 0,
            miners: 0,
            smelters: 0,
            militia: 0,
        },
        inventory: {
            food: config.initial.inventory.food,
            fish: config.initial.inventory.fish,
            tools: [],
            planks: config.initial.inventory.planks,
            wood: config.initial.inventory.wood,
            stone: config.initial.inventory.stone,
            bricks: config.initial.inventory.bricks,
            charcoal: config.initial.inventory.charcoal,
            ironOre: config.initial.inventory.ironOre,
            ironIngots: config.initial.inventory.ironIngots,
            fiber: config.initial.inventory.fiber,
            clothRope: config.initial.inventory.clothRope,
            leather: config.initial.inventory.leather,
            weapons: config.initial.inventory.weapons,
            goldOre: config.initial.inventory.goldOre,
            coins: config.initial.inventory.coins,
        },
        production: {
            lumberCamp: {
                work: 0,
            },
            farm: {
                progress: [],
            },
            hunt: {
                progress: [],
            },
            fishing: {
                progress: [],
            },
            smokehouse: {
                progress: [],
            },
            weaver: {
                progress: [],
            },
            sawmill: {
                work: 0,
            },
            quarry: {
                progress: [],
            },
            masonry: {
                progress: [],
            },
            charcoalPit: {
                progress: [],
            },
            ironMine: {
                progress: [],
            },
            smelter: {
                progress: [],
            },
            blacksmith: {
                progress: [],
                task: [],
                targetTools: config.defaults.blacksmithToolTarget,
                targetWeapons: config.defaults.blacksmithWeaponTarget,
            },
            goldMine: {
                progress: [],
            },
            mint: {
                progress: [],
            },
            population: {
                growth: 0,
                lastStarvationDeaths: 0,
                lastColdDeaths: 0,
                lastStarvationWarningTick: -1,
                lastColdWarningTick: -1,
                houseFoodTargetHours: config.defaults.houseFoodTargetHours,
                houseHeatTargetHours: config.defaults.houseHeatTargetHours,
            },
            ecology: {
                wildLifeTick: 0,
                treeTick: 0,
                reseedTick: 0,
                foresterTick: 0,
                migrationTick: 0,
                fishTick: 0,
            },
        },
        houses: [],
        regionResources: extractRegionResources(regionTiles),
        tools: {
            farmers: [],
            hunters: [],
        },
        toolIdCounter: 0,
        personIdCounter: 0,
        houseIdCounter: 0,
        resourceStats: createResourceStats(resourceWindow),
        construction: {
            queue: [],
            nextId: 0,
        },
        assistantLogistics: {
            v: 1,
            slots: [],
        },
    };
    settlement.houses = [
        ...createHouses(settlement, settlement.buildings.houses, 'wooden'),
        ...createHouses(settlement, settlement.buildings.stoneHouse, 'stone'),
    ];
    setPopulation(settlement, config.initial.settlement.population);
    settlement.inventory.tools = Array.from({ length: config.initial.inventory.tools }, () =>
        createToolItem(settlement),
    );
    return settlement;
}
